<template>
  <div class="animated fadeIn">
    <b-card class="card-border px-2 mt-4">
      <!-- Filter  -->
      <b-row class="mt-2 mb-4">
        <b-col sm="12" md="3" lg="3">
          <b-button
            v-b-popover.hover.right="'Toggle to show/hide filter options'"
            v-b-toggle.collapse-1
            class="filter"
          >
            FILTER OPTIONS
          </b-button>
        </b-col>
        <b-col sm="12">
          <!-- Collapsible Filter Options -->
          <b-collapse id="collapse-1" class="mt-2">
            <b-card>
              <b-row no-gutters>
                <b-col lg="6" md="10" sm="12">
                  <b-form-group label="Company" :description="filterCompanyDescription">
                    <v-select
                      class="style-chooser"
                      label="text"
                      placeholder=" - Please select - "
                      :options="companyAccessOptions"
                      :reduce="(company) => company.value"
                      v-model="filterBy.company"
                    >
                      <template v-slot:no-options="{ search, searching }">
                        <template v-if="searching">
                          No results found for
                          <em>
                            <strong>{{ search }}</strong>
                          </em>
                        </template>
                        <em :style="{ opacity: 0.5 }" v-else>
                          Start typing to search for a company
                        </em>
                      </template>
                    </v-select>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row no-gutters>
                <b-col lg="4" md="6" sm="12" class="mr-4">
                  <b-form-group label="Date From">
                    <input
                      type="month"
                      class="form-control"
                      v-model="filterBy.dateFrom"
                    />
                  </b-form-group>
                </b-col>
                <b-col lg="4" md="6" sm="12">
                  <b-form-group label="Date To">
                    <input type="month" class="form-control" v-model="filterBy.dateTo" />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row no-gutters>
                <b-col sm="12">
                  <b-button class="mr-1" variant="success" @click="retrieveData">
                    Generate
                  </b-button>
                  <b-button class="mr-1" variant="primary" @click="resetFilters">
                    Reset
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
        </b-col>
      </b-row>

      <b-card-title><i class="icon-home"></i> &nbsp;{{ title }}</b-card-title>
      <loading
        :active.sync="isLoading"
        loader="spinner"
        color="#20A8D8"
        :is-full-page="false"
      />

      <!-- Asset Metrics -->
      <b-row class="my-4 pb-0" no-gutters v-if="!isRetailer">
        <b-col lg="3" md="5" sm="12" class="mr-md-2 mb-2 mb-lg-0">
          <b-card no-body class="bg-light h-100">
            <b-card-body class="pb-0 px-3">
              <h4 class="mb-3 assetMetricsFont">{{ totalAssets.toLocaleString() }}</h4>
              <p class="stat-label-dark">TOTAL ASSETS</p>
              <p class="stat-caption-dark">Total count of accountable assets</p>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col lg="2" md="5" sm="12" class="mr-md-2 mb-2 mb-lg-0">
          <b-card no-body class="bg-on-hand h-100">
            <b-card-body class="pb-0 px-3">
              <h4 class="mb-3 assetMetricsFont">{{ onHandMetrics.toLocaleString() }}</h4>
              <p class="stat-label-dark">ON-HAND</p>
              <p class="stat-caption-dark">Total physical count available</p>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col lg="2" md="5" sm="12" class="mr-md-2 mb-2 mb-lg-0">
          <b-card no-body class="bg-dispatch h-100">
            <b-card-body class="pb-0 px-3">
              <h4 class="mb-3 assetMetricsFont">
                {{ dispatchedMetrics.toLocaleString() }}
              </h4>
              <p class="stat-label-dark">TRANSFERRED OUT</p>
              <p class="stat-caption-dark">Total dispatched assets</p>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col lg="2" md="5" sm="12" class="mr-md-2 mb-2 mb-lg-0">
          <b-card no-body class="bg-in-transit h-100">
            <b-card-body class="pb-0 px-3">
              <h4 class="mb-3 assetMetricsFont">
                {{ inTransitMetrics }}
              </h4>
              <p class="stat-label-dark">IN-TRANSIT</p>
              <p class="stat-caption-dark">Total in-transit assets</p>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col lg="2" md="5" sm="12">
          <b-card no-body class="bg-received h-100">
            <b-card-body class="pb-0 px-3">
              <h4 class="mb-3 assetMetricsFont">
                {{ receivedMetrics.toLocaleString() }}
              </h4>
              <p class="stat-label-dark">TRANSFERRED IN</p>
              <p class="stat-caption-dark">Total received assets</p>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="my-4 pb-0" no-gutters v-else>
        <b-col lg="3" md="5" sm="12" class="mr-md-2 mb-2 mb-md-0">
          <b-card no-body class="bg-on-hand h-100">
            <b-card-body class="pb-0 px-3">
              <h4 class="mb-3 assetMetricsFont">{{ onHandMetrics.toLocaleString() }}</h4>
              <p class="stat-label-dark">ON-HAND</p>
              <p class="stat-caption-dark">Total Assets Onsite</p>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col lg="3" md="5" sm="12">
          <b-card no-body class="bg-received h-100">
            <b-card-body class="pb-0 px-3">
              <h4 class="mb-3 assetMetricsFont">
                {{ receivedMetrics.toLocaleString() }}
              </h4>
              <p class="stat-label-dark">RECEIVED</p>
              <p class="stat-caption-dark">Total count of received assets</p>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-card>

    <b-card class="card-border px-2" v-if="hasMetricsData && analytics.length > 0">
      <ConfiguredCharts
        :chartOptions="analytics"
        :metricsData="metricsData"
        :assetTypeOptions="allAssetTypeOptions"
        :selectedCompany="filterBy.company.id"
      ></ConfiguredCharts
    ></b-card>

    <b-card class="card-border px-2">
      <!-- Asset Summary -->
      <b-tabs v-if="companyType === 'Asset Service Provider'">
        <b-tab title="Assets-On-Locations" active>
          <b-card-text>
            <AssetsOnLocation :dashboard="dashboard" :totalAssets="totalAssets" />
          </b-card-text>
        </b-tab>
      </b-tabs>
      <b-tabs v-else-if="companyType === 'Asset User'">
        <b-tab title="Assets-On-Locations" active>
          <b-card-text>
            <AssetsOnLocation :dashboard="dashboard" :totalAssets="totalAssets" />
          </b-card-text>
        </b-tab>
        <b-tab title="Delayed Transfer">
          <b-card-text>
            <DelayedTransferSummary :dashboard="dashboard" />
          </b-card-text>
        </b-tab>
        <b-tab title="Deposit">
          <b-card-text>
            <DepositSummary :dashboard="dashboard" />
          </b-card-text>
        </b-tab> </b-tabs
    ></b-card>

    <!-- Assets-On-Hire -->
    <b-card
      class="card-border px-2"
      v-if="assetsOnHire.length > 0 && !isScanner && !isRetailer"
    >
      <AssetsOnHire :filterBy="filterBy" :assetsOnHire="assetsOnHire" />
    </b-card>

    <!-- Asset Location Map -->
    <b-card class="card-border px-2">
      <div class="my-2" v-if="!isLoading">
        <GoogleMap
          :assetLocationOverlayStr="assetLocationMap"
          :company="companyParam"
          :companyOptions="allCompanyOptions"
          :storageLocationOptions="allStorageLocationOptions"
          :assetTypeOptions="allAssetTypeOptions"
          :allConnectionsObj="allConnectionsObj"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
// Utils
import { DateUtil } from "@/utils/dateutil";
import { DropDownItemsUtil } from "@/utils/dropDownItemsUtil";

// API
import dashboardApi from "@/api/dashboardApi";
import analyticsApi from "@/api/analyticsApi";

// DAO
import dashboardDAO from "@/database/dashboard";

// Others
import config from "@/config/env-constants";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import moment from 'moment';
import _ from "lodash";

export default {
  name: "dashboard",
  components: {
    Loading,
    GoogleMap: () => import("@/views/commons/GoogleMap"),
    AssetsOnLocation: () => import("@/views/commons/dashboard/AssetsOnLocation"),
    DelayedTransferSummary: () =>
      import("@/views/commons/dashboard/DelayedTransferSummary"),
    DepositSummary: () => import("@/views/commons/dashboard/DepositSummary"),
    AssetsOnHire: () => import("@/views/commons/dashboard/AssetsOnHire"),
    ConfiguredCharts: () => import("@/views/commons/charts/ConfiguredCharts"),
  },
  data() {
    return {
      // Filter Options
      companyAccessOptions: [],
      yearOptions: [],
      monthOptions: [],

      filterBy: {
        company: { ...config.companyDefaultValue },
        dateFrom: "",
        dateTo: "",
      },

      companyParam: {
        name: "",
        id: "",
      },
      dateFromParam: moment(''),
      dateToParam: moment(''),

      // Asset Location Map
      allCompanyOptions: [],
      allStorageLocationOptions: [],
      allAssetTypeOptions: [],

      allCompaniesObj: {},
      allStorageLocationsObj: {},
      allConnectionsObj: {},
      allAssetTypesObj: {},

      dashboard: {},
      metricsData: {},

      isSuperAdmin: this.$store.getters.isSuperAdmin,
      isScanner: this.$store.getters.isScanner,
      isRetailer: this.$store.getters.isRetailer,
      loggedUser: this.$store.getters.loggedUser,
      loggedUserCompany: this.$store.getters.loggedUserCompany,
      analytics: [],
      // Check for loader
      isLoading: false,

      // Listeners
      dashboardBatchesListener: null,
      assetPoolListener: null,
      dispatchListener: null,
      inventorySessionListener: null,

      removalDebounceTimer: null
    };
  },
  computed: {
    title() {
        return this.dateFromParam.isValid()
            ? this.dateFromParam.isSame(this.dateToParam)
                ? `${this.companyParam.name} Asset Summary for ${this.dateFromParam.format('MMMM YYYY')}`
                : `${this.companyParam.name} Asset Summary for ${this.dateFromParam.format('MMMM YYYY')} to ${this.dateToParam.format('MMMM YYYY')}`
            : `${this.companyParam.name} Asset Summary`;
    },
    totalAssets() {
      let dashboard = this.dashboard ? this.dashboard : {};
      let assetMetrics = dashboard.assetMetrics ? dashboard.assetMetrics : {};
      return assetMetrics.total ? assetMetrics.total : 0;
    },
    onHandMetrics() {
      let dashboard = this.dashboard ? this.dashboard : {};
      let assetMetrics = dashboard.assetMetrics ? dashboard.assetMetrics : {};
      return assetMetrics.onHand ? assetMetrics.onHand : 0;
    },
    dispatchedMetrics() {
      let dashboard = this.dashboard ? this.dashboard : {};
      let assetMetrics = dashboard.assetMetrics ? dashboard.assetMetrics : {};
      return assetMetrics.dispatched ? assetMetrics.dispatched : 0;
    },
    receivedMetrics() {
      let dashboard = this.dashboard ? this.dashboard : {};
      let assetMetrics = dashboard.assetMetrics ? dashboard.assetMetrics : {};
      return assetMetrics.received ? this.dashboard.assetMetrics.received : 0;
    },
    inTransitMetrics() {
      let dashboard = this.dashboard ? this.dashboard : {};
      let assetMetrics = dashboard.assetMetrics ? dashboard.assetMetrics : {};
      return assetMetrics.inTransit ? assetMetrics.inTransit : 0;
    },
    assetLocationMap() {
      let dashboard = this.dashboard ? this.dashboard : {};
      let assetLocationMap = dashboard.assetLocationMap ? dashboard.assetLocationMap : {};
      return assetLocationMap ? JSON.stringify(assetLocationMap) : "";
    },
    assetsOnHire() {
      let dashboard = this.dashboard ? this.dashboard : {};
      return dashboard.assetsOnHire ? dashboard.assetsOnHire : [];
    },
    filterCompanyDescription() {
      return this.filterBy.company ? this.filterBy.company.description : "-";
    },
    companyType() {
      let companyType = "";
      let companyId = this.filterBy.company.id;
      companyType = this.allCompaniesObj[companyId]
        ? this.allCompaniesObj[companyId].type
        : "";

      if (companyType.length === 0) {
        return this.loggedUserCompany.type;
      }

      return companyType;
    },
    hasMetricsData() {
      return !_.isEmpty(this.metricsData);
    },
  },
  mounted() {
    setTimeout(async () => {
      try {
        // Don't initiate data retrieval when the account is not authenticated
        if (!this.$store.getters.isAuthenticated) {
          return;
        }

        if (this.isSuperAdmin) {
          this.analytics = [...config.chartOptions.map((x) => x.value)];
        } else {
          this.analytics = [...this.$store.getters.companyAnalytics];
        }

        // show loading indicator
        this.isLoading = true;

        // Company Filter Options
        this.allCompaniesObj = {
          ...this.$store.getters.companies,
          ...this.$store.getters.connectedCompanies,
        };
        this.allCompanyOptions = DropDownItemsUtil.retrieveCompanies(
          this.allCompaniesObj
        );

        // Storage Location Filter Options
        this.allStorageLocationsObj = {
          ...this.$store.getters.storageLocations,
          ...this.$store.getters.connectedStorageLocations,
        };
        this.allStorageLocationOptions = DropDownItemsUtil.retrieveStorageLocations(
          this.allStorageLocationsObj
        );

        // Connection Options
        this.allConnectionsObj = { ...this.$store.getters.connections };

        // Asset Type Filter Options
        this.allAssetTypesObj = { ...this.$store.getters.assetTypes };
        this.allAssetTypeOptions = DropDownItemsUtil.retrieveAssetTypes(
          this.allAssetTypesObj,
          true
        );

        this.initFilterOptions();
        await this.retrieveData();
      } catch (_error) {
        this.$toaster.error("Error loading data. Please reload the page again.");
      }

      // hide loading indicator
      this.isLoading = false;
    }, config.timeout);
  },
  watch: {
    "$store.getters.companies": {
      handler() {
        this.syncCompanies();
      },
      deep: true,
      immediate: true,
    },
    "$store.getters.connectedCompanies": {
      handler() {
        this.syncCompanies();
      },
      deep: true,
      immediate: true,
    },
    "$store.getters.storageLocations": {
      handler() {
        this.syncStorageLocations();
      },
      deep: true,
      immediate: true,
    },
    "$store.getters.connectedStorageLocations": {
      handler() {
        this.syncStorageLocations();
      },
      deep: true,
      immediate: true,
    },
    "$store.getters.connections": {
      handler(val) {
        this.allConnectionsObj = { ...val };
      },
      deep: true,
      immediate: true,
    },
    "$store.getters.assetTypes": {
      handler(val) {
        this.syncAssetTypes();
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    syncCompanies() {
      this.allCompaniesObj = {
        ...this.$store.getters.companies,
        ...this.$store.getters.connectedCompanies,
      };
      this.allCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
      this.companyAccessOptions = [];

      if (this.isSuperAdmin) {
        this.companyAccessOptions = DropDownItemsUtil.retrieveAllCompanyAccess(
          this.allCompaniesObj
        );
      } else {
        // Add Sub Companies of the Current Company
        if (
          this.loggedUser.type === config.managerRole ||
          this.loggedUser.type === config.superAdminRole
        ) {
          let currCompanyId = this.loggedUserCompany.id;
          let companiesObj = _.filter(this.allCompaniesObj, (o) => {
            return o.id === currCompanyId || o.parentCompanyId === currCompanyId;
          });
          this.companyAccessOptions = DropDownItemsUtil.retrieveAllCompanyAccess(
            companiesObj
          );
        }

        // Add Company Access Options
        if (this.loggedUser.companyAccess) {
          let companyAssetOptions = DropDownItemsUtil.retrieveAllCompanyAccess(
            this.loggedUser.companyAccess
          );

          let companyNames = _.map(this.companyAccessOptions, "text");
          _.forEach(companyAssetOptions, (o) => {
            if (!companyNames.includes(o.text)) {
              this.companyAccessOptions.push(o);
            }
          });
        }
      }
    },
    syncStorageLocations() {
      this.allStorageLocationsObj = {
        ...this.$store.getters.storageLocations,
        ...this.$store.getters.connectedStorageLocations,
      };
      this.allStorageLocationOptions = DropDownItemsUtil.retrieveStorageLocations(
        this.allStorageLocationsObj
      );
    },
    syncAssetTypes() {
      this.allAssetTypesObj = { ...this.$store.getters.assetTypes };
      this.allAssetTypeOptions = DropDownItemsUtil.retrieveAssetTypes(
        this.allAssetTypesObj,
        true
      );
    },
    initFilterOptions() {
      this.companyAccessOptions = [];

      if (this.isSuperAdmin) {
        this.companyAccessOptions = DropDownItemsUtil.retrieveAllCompanyAccess(
          this.allCompaniesObj
        );
      } else {
        // Add Sub Companies of the Current Company
        if (
          this.loggedUser.type === config.managerRole ||
          this.loggedUser.type === config.superAdminRole
        ) {
          let currCompanyId = this.loggedUserCompany.id;
          let companiesObj = _.filter(this.allCompaniesObj, (o) => {
            return o.id === currCompanyId || o.parentCompanyId === currCompanyId;
          });
          this.companyAccessOptions = DropDownItemsUtil.retrieveAllCompanyAccess(
            companiesObj
          );
        }

        // Add Company Access Options
        if (this.loggedUser.companyAccess) {
          let companyAssetOptions = DropDownItemsUtil.retrieveAllCompanyAccess(
            this.loggedUser.companyAccess
          );

          let companyNames = _.map(this.companyAccessOptions, "text");
          _.forEach(companyAssetOptions, (o) => {
            if (!companyNames.includes(o.text)) {
              this.companyAccessOptions.push(o);
            }
          });
        }
      }

      this.yearOptions = DropDownItemsUtil.retrieveYearOptions();
      this.monthOptions = DropDownItemsUtil.retrieveMonthOptions();

      this.resetFilters();
    },
    resetFilters() {
      // reset to default
      this.filterBy = {
        company: DropDownItemsUtil.getCompanyAccessItem(this.loggedUserCompany),
        dateFrom: DateUtil.currMonthYear(),
        dateTo: DateUtil.currMonthYear(),
      };

      // reset validation
      this.$validator.reset();
      this.errors.clear();
    },
    async retrieveData() {
      // show loading indicator
      this.isLoading = true;

      // update param
      this.companyParam = { ...this.filterBy.company };
      this.dateFromParam = moment(this.filterBy.dateFrom);
      this.dateToParam = moment(this.filterBy.dateTo);

      this.filterBy.statuses = ["In-Transit", "Receiving", "Received"];

      let param = {
        fromTimestamp: DateUtil.getStartOfMonthTimestamp(this.filterBy.dateFrom),
        toTimestamp: DateUtil.getEndOfMonthTimestamp(this.filterBy.dateTo),
        ...this.filterBy,
      };

      try {
        const [dashboardData] = await Promise.all([
          dashboardApi.getDashboard(this.loggedUser.id, param),
          this.getAnalyticsData(),
        ]);

        this.dashboard = dashboardData.data;

        this.dashboard.assetLocationSummary = _.orderBy(
          this.dashboard.assetLocationSummary,
          ["totalQuantity", "id"],
          ["desc", "asc"]
        );

        this.totalRows = this.dashboard.assetLocationSummary.length;

        this.dashboardBatchesListener = dashboardDAO.getDashboardListener(
          this.filterBy,
          this.dashboardBatchesListenerCallback,
          "dashboardBatches"
        );

        // Retrieve analytics data on changes
        this.assetPoolListener = dashboardDAO.getDashboardListener(
          this.filterBy,
          this.getAnalyticsData,
          "assetPoolDistributions"
        );
        this.dispatchListener = dashboardDAO.getDashboardListener(
          this.filterBy,
          this.getAnalyticsData,
          "dispatches"
        );
        this.inventorySessionListener = dashboardDAO.getDashboardListener(
          this.filterBy,
          this.getAnalyticsData,
          "inventorySessions"
        );
      } catch (_error) {
        this.$toaster.error("Error loading data. Please reload the page again");
      }

      // hide loading indicator
      this.isLoading = false;
    },
    async getAnalyticsData() {
      try {
        const { data } = await analyticsApi.getDashboardAnalytics({
          companyId: this.filterBy.company.id,
          fromTimeStamp: DateUtil.getStartOfMonthTimestamp(this.filterBy.dateFrom),
          toTimeStamp: DateUtil.getEndOfMonthTimestamp(this.filterBy.dateTo),
        });

        this.metricsData = { ...data };
      } catch (_error) {
        console.log(_error);
        this.$toaster.error("Error loading data. Please reload the page again");
      }
    },
    dashboardBatchesListenerCallback(type) {
      if (type === 'removed') {
        // Clear any previous timer
        clearTimeout(this.removalDebounceTimer);

        // Wait 5s after the *last* "removed" before running
        this.removalDebounceTimer = setTimeout(() => {
          this.retrieveData();
        }, 5000);
      }
    },
  },
};
</script>

<style scoped>
.stat-label {
  font-size: 14px;
  margin-bottom: 0px;
}

.stat-label-dark {
  font-size: 14px;
  margin-bottom: 0px;
  color: #0c243b;
  font-weight: bold;
}

.stat-caption {
  font-size: 13px !important;
  color: #0c243b;
}

.stat-caption-dark {
  font-size: 12px !important;
  color: #0c243b;
  font-weight: bold;
}

.assetMetricsFont {
  font-family: monospace;
  font-weight: bold;
  font-size: 22px;
  color: #0c243b !important;
}

/* Backgrounds */
.bg-on-hand {
  background-color: #bfecff;
}

.bg-dispatch {
  background-color: #ff8080;
}

.bg-in-transit {
  background-color: #f6f193;
}

.bg-received {
  background-color: #a1d6b2;
}
</style>
